import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, Building2, Shield } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { maskCNPJ, onlyDigits } from '@/lib/format';
import { toast } from 'sonner';

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

export default function PublicPortalPage() {
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);
  const [searchType, setSearchType] = useState('cnpj'); // 'cnpj' ou 'protocol'
  const [cnpj, setCnpj] = useState('');
  const [protocol, setProtocol] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCnpjChange = (e) => {
    const value = e.target.value;
    setCnpj(maskCNPJ(value));
  };

  const validateCNPJ = (cnpj) => {
    const cleaned = onlyDigits(cnpj);
    if (cleaned.length !== 14) return false;
    
    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1{13}$/.test(cleaned)) return false;
    
    // Validação dos dígitos verificadores
    let length = cleaned.length - 2;
    let numbers = cleaned.substring(0, length);
    const digits = cleaned.substring(length);
    let sum = 0;
    let pos = length - 7;

    for (let i = length; i >= 1; i--) {
      sum += parseInt(numbers.charAt(length - i)) * pos--;
      if (pos < 2) pos = 9;
    }

    let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(digits.charAt(0))) return false;

    length = length + 1;
    numbers = cleaned.substring(0, length);
    sum = 0;
    pos = length - 7;

    for (let i = length; i >= 1; i--) {
      sum += parseInt(numbers.charAt(length - i)) * pos--;
      if (pos < 2) pos = 9;
    }

    result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    return result === parseInt(digits.charAt(1));
  };

  const handleSearch = async () => {
    if (searchType === 'cnpj') {
      if (!cnpj.trim()) {
        toast.error('Por favor, informe o CNPJ');
        return;
      }
      
      if (!validateCNPJ(cnpj)) {
        toast.error('CNPJ inválido');
        return;
      }

      // Executar reCAPTCHA
      const token = await recaptchaRef.current?.executeAsync();
      recaptchaRef.current?.reset();

      const cleanedCnpj = onlyDigits(cnpj);
      navigate(`/consulta/cnpj/${cleanedCnpj}`, { state: { recaptchaToken: token } });
    } else {
      if (!protocol.trim()) {
        toast.error('Por favor, informe o protocolo');
        return;
      }

      // Executar reCAPTCHA
      const token = await recaptchaRef.current?.executeAsync();
      recaptchaRef.current?.reset();

      navigate(`/consulta/protocolo/${protocol.trim()}`, { state: { recaptchaToken: token } });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Portal de Consulta</h1>
              <p className="text-sm text-slate-600">Acompanhe seus tickets de garantia</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto">
          {/* Info Card */}
          <Card className="p-6 mb-8 bg-blue-50 border-blue-200">
            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900 mb-1">Como consultar?</h2>
                <p className="text-sm text-slate-600">
                  Você pode consultar seus tickets de duas formas: informando seu <strong>CNPJ</strong> para ver todos os tickets, 
                  ou informando o <strong>Protocolo</strong> para ver um ticket específico.
                </p>
              </div>
            </div>
          </Card>

          {/* Search Card */}
          <Card className="p-8">
            <h2 className="text-xl font-bold text-slate-900 mb-6">Consultar Tickets</h2>

            {/* Search Type Selector */}
            <div className="flex gap-3 mb-6">
              <Button
                variant={searchType === 'cnpj' ? 'default' : 'outline'}
                onClick={() => setSearchType('cnpj')}
                className="flex-1"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Por CNPJ
              </Button>
              <Button
                variant={searchType === 'protocol' ? 'default' : 'outline'}
                onClick={() => setSearchType('protocol')}
                className="flex-1"
              >
                <FileText className="w-4 h-4 mr-2" />
                Por Protocolo
              </Button>
            </div>

            {/* CNPJ Search */}
            {searchType === 'cnpj' && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="cnpj">CNPJ</Label>
                  <Input
                    id="cnpj"
                    type="text"
                    placeholder="00.000.000/0000-00"
                    value={cnpj}
                    onChange={handleCnpjChange}
                    onKeyPress={handleKeyPress}
                    maxLength={18}
                    className="text-lg"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Digite o CNPJ da sua empresa para ver todos os tickets
                  </p>
                </div>
              </div>
            )}

            {/* Protocol Search */}
            {searchType === 'protocol' && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="protocol">Protocolo</Label>
                  <Input
                    id="protocol"
                    type="text"
                    placeholder="Ex: TKT-2026-000001"
                    value={protocol}
                    onChange={(e) => setProtocol(e.target.value.toUpperCase())}
                    onKeyPress={handleKeyPress}
                    className="text-lg"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Digite o número do protocolo do seu ticket
                  </p>
                </div>
              </div>
            )}

            {/* Search Button */}
            <Button
              onClick={handleSearch}
              disabled={loading}
              className="w-full mt-6 h-12 text-base"
              size="lg"
            >
              <Search className="w-5 h-5 mr-2" />
              {loading ? 'Consultando...' : 'Consultar'}
            </Button>
          </Card>

          {/* reCAPTCHA */}
          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-500">
            <Shield className="w-3 h-3" />
            <span>Protegido por reCAPTCHA</span>
          </div>
          <ReCAPTCHA
            ref={recaptchaRef}
            size="invisible"
            sitekey={RECAPTCHA_SITE_KEY}
          />

          {/* Help Text */}
          <div className="mt-8 text-center text-sm text-slate-600">
            <p>
              Está com dúvidas? Entre em contato com a empresa responsável pelo seu atendimento.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
